import { CheckIcon } from '@heroicons/react/24/outline';
import { REFINEMENT_STEPS, type StepId } from './spark.shared';

interface RefinementStepperProps {
    stepIndex: number;
    stepDone: Record<StepId, boolean>;
    /** Navigation is disabled while a step has an upload in flight. */
    locked: boolean;
    onSelect: (index: number) => void;
}

/** Top nav for the refinement wizard: one pill per step, with completion ticks
 *  and forward-navigation gating (a step is reachable only once every prior
 *  step is done). */
export function RefinementStepper({
    stepIndex,
    stepDone,
    locked,
    onSelect
}: RefinementStepperProps) {
    return (
        <div className="flex items-center justify-center gap-2 px-5 py-4 border-b border-gray-200 flex-shrink-0">
            {REFINEMENT_STEPS.map((step, index) => {
                const Icon = step.icon;
                const isActive = index === stepIndex;
                const isPast = index < stepIndex;
                const reachable =
                    index <= stepIndex ||
                    REFINEMENT_STEPS.slice(0, index).every((s) => stepDone[s.id]);
                return (
                    <div key={step.id} className="flex items-center">
                        <button
                            type="button"
                            disabled={!reachable || locked}
                            onClick={() => reachable && !locked && onSelect(index)}
                            aria-current={isActive ? 'step' : undefined}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 ${
                                isActive
                                    ? 'bg-gray-900 text-white cursor-pointer'
                                    : isPast
                                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer'
                                      : reachable
                                        ? 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700 cursor-pointer'
                                        : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5" />
                            <span>{step.label}</span>
                            {stepDone[step.id] && (
                                <CheckIcon className="w-3.5 h-3.5 text-green-500" />
                            )}
                        </button>
                        {index < REFINEMENT_STEPS.length - 1 && (
                            <div
                                className={`w-6 h-px mx-1 ${isPast ? 'bg-gray-300' : 'bg-gray-200'}`}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}
